import './ai-chat-composer-tip';

import type {
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import type { AIModelService } from '@affine/core/modules/ai-button/services/models';
import type {
  ServerService,
  SubscriptionService,
} from '@affine/core/modules/cloud';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type {
  ContextWorkspaceEmbeddingStatus,
  CopilotChatHistoryFragment,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import type { EditorHost } from '@blocksuite/affine/std';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { uuidv4 } from '@blocksuite/affine/store';
import type {
  FeatureFlagService,
  NotificationService,
} from '@blocksuite/affine-shared/services';
import { DATABASE_LARGE_DATA_POLICY } from '@streamlain/database-identity';
import { css, html, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';

import type { BotOptionData } from '../../chat-panel/ai-title';
import {
  type AIChatParams,
  AIProvider,
  type AISendParams,
} from '../../provider';
import { isAttachment } from '../../utils/attachment';
import {
  extractAutomationMetadata,
  isAutomationBlock,
} from '../../utils/automation-metadata';
import {
  extractDatabaseMetadata,
  findDatabaseById,
  isDatabaseBlock,
} from '../../utils/database-metadata';
import {
  applyDatabaseRowSelectionContext,
  normalizeDatabaseContextAttachments,
} from '../../utils/database-row-selection-context';
import {
  extractCanvasSelectionContext,
  filterCanvasSelectionsByCoveredBlockIds,
} from '../../utils/extract';
import {
  dispatchMailSelectionContextRemove,
  getCurrentMailSelectionContext,
  isMailSelectionContextEvent,
  MAIL_SELECTION_CONTEXT_EVENT,
  type MailMessageAttachment,
  type MailSelectionContextEventDetail,
  MAX_MAIL_CONTEXT_ATTACHMENTS,
} from '../../utils/mail-selection-context';
import { extractPdfInfo } from '../../utils/pdf-page-count';
import type { SearchMenuConfig } from '../ai-chat-add-context';
import type {
  AttachmentChip,
  ChatChip,
  CollectionChip,
  DocChip,
  DocDisplayConfig,
  FileChip,
  SelectedContextChip,
  TagChip,
} from '../ai-chat-chips';
import {
  findChipIndex,
  isAttachmentChip,
  isCollectionChip,
  isDocChip,
  isFileChip,
  isSelectedContextChip,
  isTagChip,
  omitChip,
} from '../ai-chat-chips';
import type {
  AutomationAttachment,
  CanvasAIChatContextMode,
  CanvasSelectionItem,
  CodeSelectionAttachment,
  DatabaseAttachment,
  DatabaseVisibleRowWindow,
  ExternalChatContextDoc,
  PdfAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import type {
  AIChatInputContext,
  AINetworkSearchConfig,
  AIReasoningConfig,
} from '../ai-chat-input';
import { MAX_IMAGE_COUNT } from '../ai-chat-input/const';
import {
  extractLatestGoalPlanFromMessages,
  extractLatestGoalTreeFromMessages,
  type GoalPlanHandle,
} from '../goal-tree/goal-tree-context';
import {
  FullscreenDatabaseAttachmentCache,
  FullscreenDatabaseContextContract,
} from './fullscreen-database-attachment-cache';

/**
 * sl-z4tb: Module-level cache for fullscreen attachment with row selection.
 * Survives element destruction when keyed() recreates AIChatContent on new thread.
 * Strictly keyed by (docId, blockId) so equal block IDs in different documents
 * cannot restore each other's row or column scope.
 */
const cachedFullscreenAttachments = new FullscreenDatabaseAttachmentCache();
const fullscreenDatabaseContext = new FullscreenDatabaseContextContract(
  cachedFullscreenAttachments
);

function databaseAttachmentSignature(attachment: DatabaseAttachment): string {
  return JSON.stringify({
    docId: attachment.docId ?? null,
    blockId: attachment.blockId,
    bounds: attachment.bounds ?? null,
    source: attachment.source,
    priority: attachment.priority,
    scope: attachment.scope,
    columns: attachment.columns,
    projectedColumnIds: attachment.projectedColumnIds ?? [],
    totalColumnCount: attachment.totalColumnCount ?? attachment.columns.length,
    selectedRowIds: attachment.selectedRowIds ?? [],
    selectedRowLabel: attachment.selectedRowLabel ?? null,
    visibleRowWindow: attachment.visibleRowWindow ?? null,
  });
}

function automationAttachmentSignature(
  attachment: AutomationAttachment
): string {
  return JSON.stringify({
    docId: attachment.docId ?? null,
    blockId: attachment.blockId,
    bounds: attachment.bounds ?? null,
    source: attachment.source,
    priority: attachment.priority,
  });
}

function pdfContextAttachmentSignature(
  attachment: PdfContextAttachment
): string {
  return JSON.stringify({
    blockId: attachment.blockId,
    blobKey: attachment.blobKey,
    docId: attachment.docId,
    source: attachment.source,
    priority: attachment.priority,
  });
}

function mailAttachmentSignature(attachment: MailMessageAttachment): string {
  return JSON.stringify({
    id: attachment.id,
    workspaceId: attachment.workspaceId,
    accountId: attachment.accountId,
    folderId: attachment.folderId,
    subject: attachment.subject,
    sender: attachment.sender,
    fromAddress: attachment.fromAddress ?? null,
    snippet: attachment.snippet ?? null,
    receivedAt: attachment.receivedAt ?? null,
    sentAt: attachment.sentAt ?? null,
    dateLabel: attachment.dateLabel ?? null,
    source: attachment.source,
    priority: attachment.priority,
  });
}

function attachmentListsEqual<T>(
  left: readonly T[],
  right: readonly T[],
  signature: (item: T) => string
): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) => signature(item) === signature(right[index])
  );
}

function canvasSelectionSignature(
  selection: AIChatInputContext['canvasSelections'][number]
): string {
  const normalizeItem = (item: CanvasSelectionItem): unknown => ({
    id: item.id,
    kind: item.kind,
    type: item.type,
    role: item.role ?? null,
    label: item.label,
    text: item.text ?? null,
    parentId: item.parentId ?? null,
    conversationParentId: item.conversationParentId ?? null,
    depth: item.depth ?? null,
    childCount: item.childCount ?? null,
    groupId: item.groupId ?? null,
    children: item.children?.map(normalizeItem) ?? [],
  });

  return JSON.stringify({
    docId: selection.docId,
    source: selection.source,
    priority: selection.priority,
    elementIds: [...selection.elementIds].sort(),
    blockIds: [...selection.blockIds].sort(),
    items: selection.items.map(normalizeItem),
  });
}

function flattenCanvasSelectionItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  const out: CanvasSelectionItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) {
      out.push(...flattenCanvasSelectionItems(item.children));
    }
  }
  return out;
}

function canvasSelectionLabel(items: CanvasSelectionItem[]) {
  if (items.length === 1) {
    return items[0].label;
  }

  return I18n.t('com.streamlain.ai.context.canvas.object-many', {
    count: items.length,
  });
}

function normalizeCanvasSelectionTree(
  items: CanvasSelectionItem[],
  parentId: string | null = null,
  depth = 0
): CanvasSelectionItem[] {
  return items.map(item => {
    const children = item.children?.length
      ? normalizeCanvasSelectionTree(item.children, item.id, depth + 1)
      : undefined;
    return {
      ...item,
      parentId,
      depth,
      childCount: children?.length ?? 0,
      children: children?.length ? children : undefined,
    };
  });
}

function removeCanvasSelectionItemFromTreeResult(
  items: CanvasSelectionItem[],
  itemId: string
) {
  let changed = false;
  const nextItems: CanvasSelectionItem[] = [];

  for (const item of items) {
    if (item.id === itemId) {
      changed = true;
      continue;
    }

    if (!item.children?.length) {
      nextItems.push(item);
      continue;
    }

    const childResult = removeCanvasSelectionItemFromTreeResult(
      item.children,
      itemId
    );
    if (!childResult.changed) {
      nextItems.push(item);
      continue;
    }

    changed = true;
    nextItems.push(...childResult.items);
  }

  return {
    items: normalizeCanvasSelectionTree(nextItems),
    changed,
  };
}

function removeCanvasSelectionItemFromTree(
  items: CanvasSelectionItem[],
  itemId: string
): CanvasSelectionItem[] {
  return removeCanvasSelectionItemFromTreeResult(items, itemId).items;
}

function canvasSelectionListsEqual(
  left: AIChatInputContext['canvasSelections'],
  right: AIChatInputContext['canvasSelections']
): boolean {
  return attachmentListsEqual(left, right, canvasSelectionSignature);
}

export class AIChatComposer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    :host {
      background: transparent;
    }

    .chat-panel-footer {
      margin: 8px 0px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      color: var(--streamlain-text-secondary-color);
      font-size: 10px;
      text-align: center;
      user-select: none;
      background: transparent;
    }

    .chat-panel-footer ai-chat-composer-tip {
      width: 100%;
    }

    .chat-panel-composer-input-stack {
      position: relative;
      width: 100%;
      min-width: 0;
    }
  `;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor autoFocusInput = false;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor createSession!: () => Promise<
    CopilotChatHistoryFragment | undefined
  >;

  @property({ attribute: false })
  accessor chatContextValue!: AIChatInputContext;

  @property({ attribute: false })
  accessor goalPlanOverride: GoalPlanHandle | null = null;

  @property({ attribute: false })
  accessor targetModeEnabled = false;

  @property({ attribute: false })
  accessor goalTabActive = false;

  @property({ attribute: false })
  accessor excludedCanvasSelectionBlockIds: string[] = [];

  @property({ attribute: false })
  accessor onRequestClose: (() => void) | undefined;

  @property({ attribute: false })
  accessor onGoalResume:
    | ((resumeData?: Record<string, unknown>) => void | Promise<void>)
    | null = null;

  @property({ attribute: false })
  accessor onGoalCancel: (() => void | Promise<void>) | null = null;

  @property({ attribute: false })
  accessor onGoalPause: (() => void | Promise<void>) | null = null;

  @property({ attribute: false })
  accessor externalContextDocs: ExternalChatContextDoc[] = [];

  @property({ attribute: false })
  accessor updateContext!: (context: Partial<AIChatInputContext>) => void;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor onChatSuccess: (() => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor onChatStart: (() => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor onChatError: ((error: unknown) => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor trackOptions: BlockSuitePresets.TrackerOptions | undefined;

  @property({ attribute: false })
  accessor bots: BotOptionData[] = [];

  @property({ attribute: false })
  accessor onSelectBot: ((botId: string | null) => void) | undefined;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService!: WorkspaceDialogService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor aiDraftService: AIDraftService | undefined;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  @state()
  accessor chips: ChatChip[] = [];

  @state()
  accessor isChipsCollapsed = false;

  @state()
  accessor embeddingCompleted = false;

  private _contextId: string | undefined = undefined;

  /**
   * Local synchronous copy of fullscreen attachment.
   * Needed because updateContext() goes through parent Lit render (async),
   * but _handleSelectionChange fires immediately via RxJS subscription.
   * Without this, SelectionChange reads stale chatContextValue and overwrites
   * the fullscreen attachment before the parent re-renders.
   *
   * sl-z4tb: Also backed by module-level cache so row selection survives
   * element destruction (keyed() in ChatPanel recreates AIChatContent on new thread).
   */
  private _fullscreenAttachment: DatabaseAttachment | null = null;

  private _dismissedFullscreenPdfBlockId: string | null = null;

  /** sl-z4tb: Update both instance and module-level cache */
  private _setFullscreenAttachment(att: DatabaseAttachment | null): void {
    const previous = this._fullscreenAttachment;
    this._fullscreenAttachment = att;
    // Cache attachments that have row selection for cross-instance survival
    if (att && att.selectedRowIds && att.selectedRowIds.length > 0) {
      fullscreenDatabaseContext.store(this.workspaceId, att);
    } else if (att === null && previous?.docId) {
      fullscreenDatabaseContext.clear(
        this.workspaceId,
        previous.docId,
        previous.blockId
      );
    }
    // When att exists but has no rows, DON'T clear the cache —
    // the cached version with rows is more valuable
  }

  private _clearFullscreenDatabaseAttachments(): void {
    const current = this.chatContextValue.databaseAttachments || [];
    const withoutFullscreen = current.filter(db => db.source !== 'fullscreen');
    this._setFullscreenAttachment(null);
    if (withoutFullscreen.length !== current.length) {
      this.updateContext({ databaseAttachments: withoutFullscreen });
    }
  }

  /**
   * sl-uq2j-fix: Separate cleanup for selection tracking subscription.
   * Cannot use _disposables because that only cleans up on disconnectedCallback.
   * Selection tracking must re-subscribe on every host change (doc navigation).
   */
  private _selectionTrackingCleanup: (() => void) | null = null;

  private _pollEmbeddingStatusAbortController: AbortController | null = null;

  override render() {
    const goalPlan = this.targetModeEnabled
      ? (this.goalPlanOverride ??
        extractLatestGoalPlanFromMessages(this.chatContextValue.messages))
      : null;
    const goalTree = this.targetModeEnabled
      ? (goalPlan?.goalTree ??
        extractLatestGoalTreeFromMessages(this.chatContextValue.messages))
      : null;
    return html`
      <div class="chat-panel-composer-input-stack">
        <ai-chat-input
          .autoFocusInput=${this.autoFocusInput}
          .independentMode=${this.independentMode}
          .canvasAIChatContextMode=${this.canvasAIChatContextMode}
          .host=${this.host}
          .workspaceId=${this.workspaceId}
          .docId=${this.docId}
          .session=${this.session}
          .goalTree=${goalTree}
          .goalStatus=${goalPlan?.status ?? null}
          .goalId=${goalPlan?.goalId ?? null}
          .goalBlocker=${goalPlan?.blocker ?? null}
          .targetModeEnabled=${this.targetModeEnabled}
          .goalTabActive=${this.goalTabActive}
          .onRequestClose=${this.onRequestClose}
          .onGoalResume=${this.onGoalResume}
          .onGoalCancel=${this.onGoalCancel}
          .onGoalPause=${this.onGoalPause}
          .chips=${this.chips}
          .isChipsCollapsed=${this.isChipsCollapsed}
          .addChip=${this.addChip}
          .updateChip=${this.updateChip}
          .removeChip=${this.removeChip}
          .toggleChipsCollapse=${this.toggleChipsCollapse}
          .addImages=${this.addImages}
          .addPdf=${this.addPdf}
          .removePdf=${this.removePdfAttachment}
          .visibleDatabaseAttachments=${this._getVisibleDatabaseAttachments()}
          .removeDatabaseAttachment=${this.removeDatabaseAttachment}
          .visibleAutomationAttachments=${this._getVisibleAutomationAttachments()}
          .removeAutomationAttachment=${this.removeAutomationAttachment}
          .removeWorkflowAttachment=${this.removeWorkflowAttachment}
          .removeCodeSelection=${this.removeCodeSelection}
          .removeMailAttachment=${this.removeMailAttachment}
          .removePdfContextAttachment=${this.removePdfContextAttachment}
          .removeCanvasSelectionItem=${this.removeCanvasSelectionItem}
          .removeCanvasSelectionItems=${this.removeCanvasSelectionItems}
          .createSession=${this.createSession}
          .chatContextValue=${this.chatContextValue}
          .updateContext=${this.updateContext}
          .externalContextDocs=${this.externalContextDocs}
          .networkSearchConfig=${this.networkSearchConfig}
          .reasoningConfig=${this.reasoningConfig}
          .docDisplayConfig=${this.docDisplayConfig}
          .searchMenuConfig=${this.searchMenuConfig}
          .serverService=${this.serverService}
          .affineFeatureFlagService=${this.affineFeatureFlagService}
          .aiDraftService=${this.aiDraftService}
          .aiToolsConfigService=${this.aiToolsConfigService}
          .notificationService=${this.notificationService}
          .subscriptionService=${this.subscriptionService}
          .aiModelService=${this.aiModelService}
          .onAISubscribe=${this.onAISubscribe}
          .affineWorkspaceDialogService=${this.affineWorkspaceDialogService}
          .portalContainer=${this.portalContainer}
          .excludedCanvasSelectionBlockIds=${this
            .excludedCanvasSelectionBlockIds}
          .onChatStart=${this.onChatStart}
          .onChatSuccess=${this.onChatSuccess}
          .onChatError=${this.onChatError}
          .bots=${this.bots}
          .onSelectBot=${this.onSelectBot}
          .trackOptions=${this.trackOptions}
        ></ai-chat-input>
      </div>
      <div class="chat-panel-footer">
        <ai-chat-composer-tip
          .tips=${[
            html`<span>${I18n.t('com.streamlain.ai.chat.disclaimer')}</span>`,
          ].filter(Boolean)}
          .loop=${false}
        ></ai-chat-composer-tip>
      </div>
    `;
  }

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.add(
      AIProvider.slots.requestOpenWithChat.subscribe(this.beforeChatContextSend)
    );
    this._disposables.add(
      AIProvider.slots.requestSendWithChat.subscribe(this.beforeChatContextSend)
    );
    this.initComposer().catch(console.error);

    // sl-57s.1.3: Focus/Selection Detection - Auto-inject selected databases
    this._setupSelectionTracking();

    // sl-57s.1.2: Fullscreen Mode - Auto-inject active database tab
    this._setupFullscreenTracking();

    // sl-28ek: Row selection in fullscreen — update context chip with selected rows
    this._setupRowSelectionTracking();
    this._setupMailSelectionTracking();
  }

  /**
   * sl-57s.1.3: Setup selection tracking for auto-injecting databases.
   * sl-uq2j-fix: Stores cleanup in _selectionTrackingCleanup (not _disposables)
   * so it can be torn down and re-subscribed on host changes (doc navigation).
   */
  private _setupSelectionTracking(): void {
    // Teardown previous subscription (host changed or re-setup)
    this._selectionTrackingCleanup?.();
    this._selectionTrackingCleanup = null;

    const host = this.host;
    if (!host) return;

    const sub = host.selection.slots.changed.subscribe(() => {
      this._handleSelectionChange();
    });
    this._selectionTrackingCleanup = () => sub.unsubscribe();

    // The composer can mount after the user has already selected database
    // rows. The selection slot only emits future changes, so hydrate the
    // current selection immediately as well.
    this._handleSelectionChange();
  }

  private _setupMailSelectionTracking(): void {
    const handler = (event: Event) => {
      if (!isMailSelectionContextEvent(event)) return;
      this._handleMailSelectionContextChange(event.detail);
    };

    window.addEventListener(MAIL_SELECTION_CONTEXT_EVENT, handler);
    this._disposables.add(() => {
      window.removeEventListener(MAIL_SELECTION_CONTEXT_EVENT, handler);
    });

    const current = getCurrentMailSelectionContext();
    if (current) {
      this._handleMailSelectionContextChange(current);
    }
  }

  private _handleMailSelectionContextChange(
    detail: MailSelectionContextEventDetail
  ): void {
    if (detail.workspaceId !== this.workspaceId) return;
    const current = this.chatContextValue.mailAttachments || [];
    const next =
      detail.action === 'update'
        ? detail.messages.slice(0, MAX_MAIL_CONTEXT_ATTACHMENTS)
        : [];
    if (attachmentListsEqual(current, next, mailAttachmentSignature)) {
      return;
    }
    this.updateContext({
      mailAttachments: next,
    });
  }

  /**
   * sl-57s.1.2: Setup fullscreen tracking for auto-injecting databases
   * sl-z4tb.4: Also check URL params on init for reload persistence
   */
  private _setupFullscreenTracking(): void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        action: 'open' | 'close' | 'tab-switch';
        databaseId: string;
        databaseName: string;
        docId: string;
      }>;
      this._handleFullscreenContextChange(customEvent.detail);
    };

    window.addEventListener('streamlain:fullscreen-context', handler);
    this._disposables.add(() => {
      window.removeEventListener('streamlain:fullscreen-context', handler);
    });

    const attachmentHandler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        action: 'open' | 'close';
        docId: string;
        attachmentId: string;
        attachmentName?: string;
        blobKey?: string;
        mimeType?: string;
      }>;
      this._handleFullscreenPdfContextChange(customEvent.detail);
    };

    window.addEventListener(
      'streamlain:fullscreen-attachment-context',
      attachmentHandler
    );
    this._disposables.add(() => {
      window.removeEventListener(
        'streamlain:fullscreen-attachment-context',
        attachmentHandler
      );
    });

    // sl-z4tb.4: On mount, check if fullscreen is already active (survives reload)
    this._restoreFullscreenContextFromUrl();
    this._restoreFullscreenPdfContextFromUrl();
  }

  /**
   * sl-z4tb.4: Restore fullscreen database context chip from URL params.
   * After reload, the CustomEvent from DatabaseFullscreenEditor may have
   * already fired before AIChatComposer mounted — read URL as fallback.
   */
  private _restoreFullscreenContextFromUrl(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const fullscreenDbId = params.get('fullscreenDatabaseId');
      if (!fullscreenDbId) return;
      if (fullscreenDbId === '__placeholder__') {
        this._clearFullscreenDatabaseAttachments();
        return;
      }
      const currentDocId = this.docId?.trim();
      if (!currentDocId) {
        this._clearFullscreenDatabaseAttachments();
        return;
      }

      // Already have a fullscreen attachment for this database in context?
      const current = this.chatContextValue.databaseAttachments || [];
      const existingState = fullscreenDatabaseContext.existingState(
        current,
        currentDocId,
        fullscreenDbId
      );
      if (existingState === 'mismatch') {
        this._clearFullscreenDatabaseAttachments();
        return;
      }
      if (existingState === 'exact') {
        return;
      }

      // sl-z4tb: Check module-level cache for row selection that survived element destruction.
      // keyed() in ChatPanel destroys AIChatContent on new-thread → instance state lost.
      // The module-level cache preserves the last attachment with selectedRowIds.
      const cached = fullscreenDatabaseContext.restore(
        this.workspaceId,
        currentDocId,
        fullscreenDbId
      );
      if (cached) {
        this._fullscreenAttachment = cached;
        const withoutFullscreen = current.filter(
          db => db.source !== 'fullscreen'
        );
        this.updateContext({
          databaseAttachments: [...withoutFullscreen, cached],
        });
        return;
      }

      // Try to extract metadata from the block (host/store may not be ready yet)
      const store = this.host?.store;
      if (store) {
        const block = findDatabaseById(store, fullscreenDbId);
        if (block) {
          const attachment = extractDatabaseMetadata(
            block,
            'fullscreen',
            'mid',
            { docId: currentDocId }
          );
          this._setFullscreenAttachment(attachment);
          const withoutFullscreen = current.filter(
            db => db.source !== 'fullscreen'
          );
          this.updateContext({
            databaseAttachments: [...withoutFullscreen, attachment],
          });
          return;
        }
        this._clearFullscreenDatabaseAttachments();
        return;
      }

      // Fallback: inject with minimal info (columns fetched by backend)
      const fallbackAttachment: DatabaseAttachment = {
        docId: currentDocId,
        blockId: fullscreenDbId,
        name: I18n.t('com.streamlain.ai.context.canvas.type.database'),
        columns: [],
        source: 'fullscreen' as const,
        priority: 'mid' as const,
        scope: 'metadata-only' as const,
      };
      this._setFullscreenAttachment(fallbackAttachment);
      const withoutFullscreen = current.filter(
        db => db.source !== 'fullscreen'
      );
      this.updateContext({
        databaseAttachments: [...withoutFullscreen, fallbackAttachment],
      });
    } catch {
      // URL parsing failed — no-op
    }
  }

  private _restoreFullscreenPdfContextFromUrl(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const attachmentId = params.get('fullscreenAttachmentId');
      if (!attachmentId) return;
      this._handleFullscreenPdfContextChange({
        action: 'open',
        docId: this.docId || '',
        attachmentId,
      });
    } catch {
      // URL parsing failed — no-op
    }
  }

  private _createFullscreenPdfContext(input: {
    docId: string;
    attachmentId: string;
    attachmentName?: string;
    blobKey?: string;
    mimeType?: string;
  }): PdfContextAttachment | null {
    const block = this.host?.store.getBlock(input.attachmentId);
    const props = (
      block?.model as {
        props?: {
          name?: string;
          sourceId?: string;
          type?: string;
        };
      }
    )?.props;
    const mimeType = input.mimeType || props?.type;
    if (mimeType && mimeType !== 'application/pdf') {
      return null;
    }
    const blobKey = input.blobKey || props?.sourceId;
    if (!blobKey) {
      return null;
    }
    return {
      docId: input.docId || this.docId || '',
      blockId: input.attachmentId,
      name: input.attachmentName || props?.name || 'document.pdf',
      blobKey,
      source: 'fullscreen',
      priority: 'mid',
    };
  }

  private _handleFullscreenPdfContextChange(detail: {
    action: 'open' | 'close';
    docId: string;
    attachmentId: string;
    attachmentName?: string;
    blobKey?: string;
    mimeType?: string;
  }): void {
    if (!detail.attachmentId) return;

    const current = this.chatContextValue.pdfContextAttachments || [];
    if (detail.action === 'close') {
      if (this._dismissedFullscreenPdfBlockId === detail.attachmentId) {
        this._dismissedFullscreenPdfBlockId = null;
      }
      const withoutFullscreen = current.filter(
        pdf =>
          !(pdf.source === 'fullscreen' && pdf.blockId === detail.attachmentId)
      );
      if (withoutFullscreen.length !== current.length) {
        this.updateContext({ pdfContextAttachments: withoutFullscreen });
      }
      return;
    }

    if (this._dismissedFullscreenPdfBlockId === detail.attachmentId) {
      return;
    }

    const pdf = this._createFullscreenPdfContext(detail);
    if (!pdf) return;
    const withoutFullscreen = current.filter(
      item => item.blockId !== detail.attachmentId
    );
    const next = [...withoutFullscreen, pdf];
    if (attachmentListsEqual(current, next, pdfContextAttachmentSignature)) {
      return;
    }
    this.updateContext({
      pdfContextAttachments: next,
    });
  }

  /**
   * sl-57s.1.2: Handle fullscreen context change
   */
  private _handleFullscreenContextChange(detail: {
    action: 'open' | 'close' | 'tab-switch';
    databaseId: string;
    databaseName: string;
    docId: string;
  }): void {
    const currentAttachments = this.chatContextValue.databaseAttachments || [];

    if (detail.action === 'close' || detail.databaseId === '__placeholder__') {
      this._clearFullscreenDatabaseAttachments();
      return;
    }
    const currentDocId = this.docId?.trim();
    const eventDocId = detail.docId?.trim();
    if (
      !currentDocId ||
      !eventDocId ||
      !fullscreenDatabaseContext.acceptsEvent(currentDocId, eventDocId)
    ) {
      this._clearFullscreenDatabaseAttachments();
      return;
    }

    // 'open' or 'tab-switch': Add/update fullscreen attachment
    const store = this.host?.store;
    const block = store ? findDatabaseById(store, detail.databaseId) : null;
    if (store && !block) {
      this._clearFullscreenDatabaseAttachments();
      return;
    }
    const extractedAttachment = block
      ? extractDatabaseMetadata(block, 'fullscreen', 'mid', {
          docId: eventDocId,
        })
      : null;
    const newAttachment: DatabaseAttachment = extractedAttachment
      ? {
          ...extractedAttachment,
          name: detail.databaseName || extractedAttachment.name,
        }
      : {
          blockId: detail.databaseId,
          docId: eventDocId,
          name:
            detail.databaseName ||
            I18n.t('com.streamlain.ai.context.canvas.type.database'),
          columns: [],
          source: 'fullscreen',
          priority: 'mid',
          scope: 'metadata-only',
        };

    // Store synchronously so _handleSelectionChange always has it
    this._setFullscreenAttachment(newAttachment);

    // Remove old fullscreen attachments, add new one
    const withoutFullscreen = currentAttachments.filter(
      db => db.source !== 'fullscreen'
    );
    const next = normalizeDatabaseContextAttachments([
      ...withoutFullscreen,
      newAttachment,
    ]);
    if (
      attachmentListsEqual(
        currentAttachments,
        next,
        databaseAttachmentSignature
      )
    ) {
      return;
    }
    this.updateContext({
      databaseAttachments: next,
    });
  }

  /**
   * sl-28ek: Setup row selection tracking for fullscreen database context
   */
  private _setupRowSelectionTracking(): void {
    const handler = (event: Event) => {
      const { databaseId, selectedRowIds, visibleRowWindow } = (
        event as CustomEvent<{
          databaseId: string;
          selectedRowIds: string[];
          selectedRowLabel: string;
          visibleRowWindow?: DatabaseVisibleRowWindow;
        }>
      ).detail;
      this._handleFullscreenRowSelection(
        databaseId,
        selectedRowIds,
        visibleRowWindow
      );
    };
    window.addEventListener('streamlain:fullscreen-row-selection', handler);
    this._disposables.add(() => {
      window.removeEventListener(
        'streamlain:fullscreen-row-selection',
        handler
      );
    });
  }

  /**
   * sl-28ek: Update fullscreen attachment with selected row info
   */
  private _handleFullscreenRowSelection(
    databaseId: string,
    selectedRowIds: string[],
    visibleRowWindow?: DatabaseVisibleRowWindow
  ): void {
    if (!this._fullscreenAttachment) return;
    const currentDocId = this.docId?.trim();
    if (
      !currentDocId ||
      !fullscreenDatabaseContext.acceptsRowSelection(
        this._fullscreenAttachment,
        currentDocId,
        databaseId
      )
    ) {
      this._clearFullscreenDatabaseAttachments();
      return;
    }

    if (
      selectedRowIds.length >
      DATABASE_LARGE_DATA_POLICY.ai.contextSelectedRowLimit
    ) {
      throw new RangeError(
        `Database AI context supports at most ${DATABASE_LARGE_DATA_POLICY.ai.contextSelectedRowLimit} explicitly selected rows; ${selectedRowIds.length} were selected. No context was sent.`
      );
    }

    this._setFullscreenAttachment({
      ...this._fullscreenAttachment,
      scope: selectedRowIds.length > 0 ? 'selected-rows' : 'visible-window',
      visibleRowWindow:
        visibleRowWindow ?? this._fullscreenAttachment.visibleRowWindow,
      selectedRowIds: selectedRowIds.length > 0 ? selectedRowIds : undefined,
      selectedRowLabel:
        selectedRowIds.length > 0
          ? `${selectedRowIds.length} selected rows`
          : undefined,
    });

    const current = this.chatContextValue.databaseAttachments || [];
    const withoutFullscreen = current.filter(db => db.source !== 'fullscreen');
    const next = normalizeDatabaseContextAttachments([
      ...withoutFullscreen,
      this._fullscreenAttachment,
    ]);
    if (attachmentListsEqual(current, next, databaseAttachmentSignature)) {
      return;
    }
    this.updateContext({
      databaseAttachments: next,
    });
  }

  /**
   * sl-57s.1.3: Handle selection change - detect selected databases and automations
   */
  private _handleSelectionChange(): void {
    const host = this.host;
    if (!host) return;

    const currentDbAttachments =
      this.chatContextValue.databaseAttachments || [];
    const currentAutoAttachments =
      this.chatContextValue.automationAttachments || [];
    const currentWorkflowAttachments =
      this.chatContextValue.workflowAttachments || [];

    // If user has manually pasted databases, don't auto-inject focus DB attachments
    const hasManualDbAttachments = currentDbAttachments.some(
      db => db.source === 'paste'
    );
    // If user has manually pasted automations, don't auto-inject focus automation attachments
    const hasManualAutoAttachments = currentAutoAttachments.some(
      a => a.source === 'paste'
    );

    // Get currently selected blocks
    const selections = host.selection.value;
    const selectedDatabases: DatabaseAttachment[] = [];
    const selectedAutomations: AutomationAttachment[] = [];
    const selectedPdfContexts: PdfContextAttachment[] = [];
    const currentCanvasSelections =
      this.chatContextValue.canvasSelections || [];
    let selectedCanvasSelections = extractCanvasSelectionContext(host);

    for (const sel of selections) {
      if ('blockId' in sel) {
        const blockId = (sel as { blockId: string }).blockId;
        const block = host.store.getBlock(blockId);
        if (!block) continue;

        if (isDatabaseBlock(block.model)) {
          selectedDatabases.push(
            applyDatabaseRowSelectionContext(
              extractDatabaseMetadata(block.model, 'focus', 'mid', {
                docId: this.docId,
              }),
              sel
            )
          );
        } else if (isAutomationBlock(block.model)) {
          selectedAutomations.push(
            extractAutomationMetadata(block.model, 'focus', 'mid', {
              docId: this.docId,
            })
          );
        } else if (
          // sl-w252: Detect PDF attachment blocks on canvas
          isAttachment(block.model) &&
          (block.model as { props?: { type?: string } }).props?.type ===
            'application/pdf'
        ) {
          const props = (
            block.model as {
              props?: {
                name?: string;
                sourceId?: string;
              };
            }
          ).props;
          selectedPdfContexts.push({
            docId: this.docId || '',
            blockId,
            name: props?.name || 'document.pdf',
            blobKey: props?.sourceId || '',
            source: 'focus',
            priority: 'mid',
          });
        }
      }
    }

    selectedCanvasSelections = filterCanvasSelectionsByCoveredBlockIds(
      selectedCanvasSelections,
      [
        ...selectedPdfContexts.map(pdf => pdf.blockId),
        ...this.excludedCanvasSelectionBlockIds,
      ],
      this.canvasAIChatContextMode
    );

    // Database focus updates (skip if manual paste exists)
    const contextUpdates: Partial<typeof this.chatContextValue> = {};
    let hasChanged = false;

    if (hasManualDbAttachments) {
      const withoutFocus = currentDbAttachments.filter(
        db => db.source !== 'focus'
      );
      if (withoutFocus.length !== currentDbAttachments.length) {
        contextUpdates.databaseAttachments = withoutFocus;
        hasChanged = true;
      }
    } else {
      // Filter out focus AND fullscreen (we'll re-add fullscreen from local field)
      const keepDb = currentDbAttachments.filter(
        db => db.source !== 'focus' && db.source !== 'fullscreen'
      );
      const newDbAttachments = normalizeDatabaseContextAttachments([
        ...keepDb,
        ...selectedDatabases,
        ...(this._fullscreenAttachment ? [this._fullscreenAttachment] : []),
      ]);
      if (
        !attachmentListsEqual(
          currentDbAttachments,
          newDbAttachments,
          databaseAttachmentSignature
        )
      ) {
        contextUpdates.databaseAttachments = newDbAttachments;
        hasChanged = true;
      }
    }

    // Automation focus updates (skip if manual paste exists)
    if (hasManualAutoAttachments) {
      const withoutFocus = currentAutoAttachments.filter(
        a => a.source !== 'focus'
      );
      if (withoutFocus.length !== currentAutoAttachments.length) {
        contextUpdates.automationAttachments = withoutFocus;
        hasChanged = true;
      }
    } else {
      const nonFocusAuto = currentAutoAttachments.filter(
        a => a.source !== 'focus'
      );
      const newAutoAttachments = [...nonFocusAuto, ...selectedAutomations];
      if (
        !attachmentListsEqual(
          currentAutoAttachments,
          newAutoAttachments,
          automationAttachmentSignature
        )
      ) {
        contextUpdates.automationAttachments = newAutoAttachments;
        hasChanged = true;
      }
    }

    const retainedWorkflowAttachments = currentWorkflowAttachments.filter(
      workflow => !workflow.automationBlockId
    );
    const seenWorkflowIds = new Set<string>();
    const selectedAutomationWorkflows = selectedAutomations.flatMap(
      automation => {
        if (
          !automation.workflowId ||
          seenWorkflowIds.has(automation.workflowId)
        ) {
          return [];
        }
        seenWorkflowIds.add(automation.workflowId);
        return [
          {
            workflowId: automation.workflowId,
            automationBlockId: automation.blockId,
            workflowName: automation.name,
            scope: 'whole-workflow' as const,
            contextKey: `automation:${automation.blockId}:${automation.workflowId}`,
            autoSelected: true,
            selectedNodes: [],
            haloNodes: [],
            edges: [],
            tokenEstimate: 0,
            source: 'focus' as const,
            priority: 'highest' as const,
          },
        ];
      }
    );
    const nextWorkflowAttachments = [
      ...retainedWorkflowAttachments,
      ...selectedAutomationWorkflows,
    ];
    if (
      !attachmentListsEqual(
        currentWorkflowAttachments,
        nextWorkflowAttachments,
        workflow =>
          `${workflow.workflowId}:${workflow.automationBlockId ?? ''}:${workflow.contextKey ?? ''}`
      )
    ) {
      contextUpdates.workflowAttachments = nextWorkflowAttachments;
      hasChanged = true;
    }

    // sl-w252: PDF context focus updates (Canvas Selection — Weg B)
    const currentPdfContexts =
      this.chatContextValue.pdfContextAttachments || [];
    const fullscreenPdfBlockIds = new Set(
      currentPdfContexts
        .filter(p => p.source === 'fullscreen')
        .map(p => p.blockId)
    );
    const nonFocusPdf = currentPdfContexts.filter(p => p.source !== 'focus');
    const newPdfContextAttachments = [
      ...nonFocusPdf,
      ...selectedPdfContexts.filter(p => !fullscreenPdfBlockIds.has(p.blockId)),
    ];
    if (
      !attachmentListsEqual(
        currentPdfContexts,
        newPdfContextAttachments,
        pdfContextAttachmentSignature
      )
    ) {
      contextUpdates.pdfContextAttachments = newPdfContextAttachments;
      hasChanged = true;
    }

    const nonFocusCanvasSelections = currentCanvasSelections.filter(
      selection => selection.source !== 'focus'
    );
    const newCanvasSelections = [
      ...nonFocusCanvasSelections,
      ...selectedCanvasSelections,
    ];
    if (
      !canvasSelectionListsEqual(currentCanvasSelections, newCanvasSelections)
    ) {
      contextUpdates.canvasSelections = newCanvasSelections;
      hasChanged = true;
      this.syncCanvasSelectionChip(newCanvasSelections);
    }

    if (hasChanged) {
      this.updateContext(contextUpdates);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._selectionTrackingCleanup?.();
    this._selectionTrackingCleanup = null;
    this._abortPollEmbeddingStatus();
  }

  protected override willUpdate(changedProperties: PropertyValues): void {
    // sl-uq2j-fix: Re-subscribe selection tracking when host changes.
    // The workspace-wide sidebar receives host updates via DetailPageChatSync
    // when the user navigates between docs. Without this, selection tracking
    // is only set up once in connectedCallback (with null/mock host) and
    // never re-initialized when a real editor host becomes available.
    if (changedProperties.has('host')) {
      this._setupSelectionTracking();
    }

    if (
      changedProperties.has('chatContextValue') &&
      changedProperties.get('chatContextValue')?.status !== 'loading' &&
      this.chatContextValue.status === 'loading' &&
      this.isChipsCollapsed === false
    ) {
      this.isChipsCollapsed = true;
    }
  }

  private readonly beforeChatContextSend = (
    params: AISendParams | AIChatParams | null
  ) => {
    if (!params) return;

    const { context, host } = params;
    const workspaceId = 'workspaceId' in params ? params.workspaceId : null;
    const targetsThisChat = host
      ? this.host === host
      : Boolean(workspaceId && workspaceId === this.workspaceId);
    if (!targetsThisChat || !context) return;

    if (context) {
      this.updateContext(context);
    }
    if (
      context.docs ||
      context.attachments ||
      context.snapshot ||
      context.combinedElementsMarkdown ||
      context.canvasSelections?.length ||
      context.html
    ) {
      // Wait for context value updated next frame
      setTimeout(() => {
        this.addSelectedContextChip().catch(console.error);
      }, 0);
    }
  };

  private readonly _getContextId = async () => {
    if (this._contextId) {
      return this._contextId;
    }

    const sessionId = this.session?.sessionId;
    if (!sessionId) return;

    const contextId = await AIProvider.context?.getContextId(
      this.workspaceId,
      sessionId
    );
    this._contextId = contextId;
    return this._contextId;
  };

  private readonly createContextId = async () => {
    if (this._contextId) {
      return this._contextId;
    }

    const sessionId = (await this.createSession())?.sessionId;
    if (!sessionId) return;

    this._contextId = await AIProvider.context?.createContext(
      this.workspaceId,
      sessionId
    );
    return this._contextId;
  };

  private readonly initChips = async () => {
    // context not initialized
    const sessionId = this.session?.sessionId;
    const contextId = await this._getContextId();
    if (!sessionId || !contextId) {
      return;
    }

    // context initialized, show the chips
    const {
      docs = [],
      files = [],
      tags = [],
      collections = [],
    } = (await AIProvider.context?.getContextDocsAndFiles(
      this.workspaceId,
      sessionId,
      contextId
    )) || {};

    const docChips: DocChip[] = docs.map(doc => ({
      docId: doc.id,
      state: 'finished',
      createdAt: doc.createdAt,
    }));

    const fileChips: FileChip[] = await Promise.all(
      files.map(async file => {
        return {
          file: new File([], file.name),
          blobId: file.blobId,
          fileId: file.id,
          state: 'finished',
          createdAt: file.createdAt,
        };
      })
    );

    const tagChips: TagChip[] = tags.map(tag => ({
      tagId: tag.id,
      state: 'finished',
      createdAt: tag.createdAt,
    }));

    const collectionChips: CollectionChip[] = collections.map(collection => ({
      collectionId: collection.id,
      state: 'finished',
      createdAt: collection.createdAt,
    }));

    const chips: ChatChip[] = [
      ...docChips,
      ...fileChips,
      ...tagChips,
      ...collectionChips,
    ].sort((a, b) => {
      const aTime = a.createdAt ?? Date.now();
      const bTime = b.createdAt ?? Date.now();
      return aTime - bTime;
    });

    this.updateChips(chips);
  };

  private readonly updateChips = (chips: ChatChip[]) => {
    this.chips = chips;
  };

  private readonly updateChip = (
    chip: ChatChip,
    options: Partial<ChatChip>
  ) => {
    const index = findChipIndex(this.chips, chip);
    if (index === -1) {
      return;
    }
    const nextChip = {
      ...chip,
      ...options,
    } as ChatChip;
    this.updateChips([
      ...this.chips.slice(0, index),
      nextChip,
      ...this.chips.slice(index + 1),
    ]);
  };

  private readonly addChip = async (
    chip: ChatChip,
    silent: boolean = false
  ) => {
    this.isChipsCollapsed = false;
    // if already exists
    const index = findChipIndex(this.chips, chip);
    if (index !== -1) {
      if (!silent) {
        this.notificationService.toast('chip already exists');
      }
      return;
    }
    this.updateChips([...this.chips, chip]);
    await this.addToContext(chip);
  };

  private readonly removeChip = async (chip: ChatChip) => {
    const chips = omitChip(this.chips, chip);
    this.updateChips(chips);
    await this.removeFromContext(chip);
  };

  private readonly addSelectedContextChip = async () => {
    const {
      attachments = [],
      snapshot,
      combinedElementsMarkdown,
      canvasSelections = [],
      docs = [],
      html,
    } = this.chatContextValue;
    await this.removeSelectedContextChip();
    const canvasLabel =
      canvasSelections.length === 1
        ? canvasSelections[0].label
        : canvasSelections.length > 1
          ? I18n.t('com.streamlain.ai.context.canvas.object-many', {
              count: canvasSelections.length,
            })
          : undefined;
    const chip: SelectedContextChip = {
      uuid: uuidv4(),
      name: canvasLabel,
      snapshot,
      combinedElementsMarkdown,
      html,
      canvasSelections,
      state: 'finished',
    };
    await Promise.all([
      this.addChip(chip, true),
      ...docs.map(docId =>
        this.addChip(
          {
            docId,
            state: 'processing',
          },
          true
        )
      ),
      ...attachments.map(attachment =>
        this.addChip(
          {
            sourceId: attachment.sourceId,
            name: attachment.name,
            state: 'processing',
          },
          true
        )
      ),
    ]);
  };

  private readonly syncCanvasSelectionChip = (
    canvasSelections: AIChatInputContext['canvasSelections']
  ) => {
    const selectedContextChip = this.chips.find(c => isSelectedContextChip(c));
    const withoutSelectedContext = this.chips.filter(
      c => !isSelectedContextChip(c)
    );

    if (canvasSelections.length === 0) {
      if (selectedContextChip) {
        this.updateChips(withoutSelectedContext);
      }
      return;
    }

    const items = canvasSelections.flatMap(selection => selection.items);
    const chip: SelectedContextChip = {
      uuid: selectedContextChip?.uuid ?? uuidv4(),
      name: canvasSelectionLabel(items),
      snapshot: null,
      combinedElementsMarkdown: null,
      html: null,
      canvasSelections,
      state: 'finished',
    };

    this.updateChips([...withoutSelectedContext, chip]);
  };

  private readonly removeSelectedContextChip = async () => {
    const selectedContextChip = this.chips.find(c => isSelectedContextChip(c));
    if (selectedContextChip) {
      await this.removeChip(selectedContextChip);
    }
  };

  private readonly removeCanvasSelectionItem = (
    chip: ChatChip,
    itemId: string
  ) => {
    this.removeCanvasSelectionItems(chip, [itemId]);
  };

  private readonly removeCanvasSelectionItems = (
    chip: ChatChip,
    itemIds: string[]
  ) => {
    if (!isSelectedContextChip(chip)) return;
    const idsToRemove = new Set(itemIds);

    const canvasSelections = (this.chatContextValue.canvasSelections || [])
      .map(selection => {
        const items = [...idsToRemove].reduce(
          (nextItems, id) => removeCanvasSelectionItemFromTree(nextItems, id),
          selection.items
        );
        const flatItems = flattenCanvasSelectionItems(items);
        return {
          ...selection,
          items,
          elementIds: flatItems
            .filter(item => item.kind === 'canvas_element')
            .map(item => item.id),
          blockIds: flatItems
            .filter(item => item.kind === 'block')
            .map(item => item.id),
          label: canvasSelectionLabel(items),
        };
      })
      .filter(selection => selection.items.length > 0);
    const selectedContextChip = this.chips.find(c => isSelectedContextChip(c));

    this.updateContext({
      ...this.chatContextValue,
      canvasSelections,
      snapshot: null,
      combinedElementsMarkdown: null,
      html: null,
    });

    if (canvasSelections.length === 0) {
      if (selectedContextChip) {
        this.updateChips(this.chips.filter(c => !isSelectedContextChip(c)));
      }
    } else if (selectedContextChip) {
      const items = canvasSelections.flatMap(selection => selection.items);
      this.updateChip(selectedContextChip, {
        name: canvasSelectionLabel(items),
        canvasSelections,
        snapshot: null,
        combinedElementsMarkdown: null,
        html: null,
      });
    }

    const host = this.host;
    if (host) {
      const currentSelection = host.selection.value;
      const filteredSelection = currentSelection.filter(
        sel =>
          !(
            'blockId' in sel &&
            idsToRemove.has((sel as { blockId: string }).blockId)
          )
      );
      if (filteredSelection.length !== currentSelection.length) {
        host.selection.set(filteredSelection);
      }
    }

    const remainingIds = canvasSelections.flatMap(selection => [
      ...selection.elementIds,
      ...selection.blockIds,
    ]);
    const gfx = this.host?.std.getOptional(GfxControllerIdentifier);
    if (gfx) {
      const currentSelectedIds = (
        gfx.selection as {
          selectedIds?: string[];
        }
      ).selectedIds;
      gfx.selection.set({
        elements: currentSelectedIds
          ? currentSelectedIds.filter(id => !idsToRemove.has(id))
          : remainingIds,
        editing: false,
      });
    }
  };

  private readonly addToContext = async (chip: ChatChip) => {
    if (isDocChip(chip)) {
      return await this.addDocToContext(chip);
    }
    if (isFileChip(chip)) {
      return await this.addFileToContext(chip);
    }
    if (isTagChip(chip)) {
      return await this.addTagToContext(chip);
    }
    if (isCollectionChip(chip)) {
      return await this.addCollectionToContext(chip);
    }
    if (isAttachmentChip(chip)) {
      return await this.addAttachmentChipToContext(chip);
    }
    return null;
  };

  private readonly addDocToContext = async (chip: DocChip) => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        throw new Error('Context not found');
      }
      await AIProvider.context.addContextDoc({
        contextId,
        docId: chip.docId,
      });
      this.updateChip(chip, {
        state: 'finished',
      });
    } catch (e) {
      this.updateChip(chip, {
        state: 'failed',
        tooltip: e instanceof Error ? e.message : 'Add context doc error',
      });
    }
  };

  private readonly addFileToContext = async (chip: FileChip) => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        throw new Error('Context not found');
      }
      const contextFile = await AIProvider.context.addContextFile(chip.file, {
        contextId,
      });
      this.updateChip(chip, {
        state: 'finished',
        blobId: contextFile.blobId,
        fileId: contextFile.id,
      });
    } catch (e) {
      this.updateChip(chip, {
        state: 'failed',
        tooltip: e instanceof Error ? e.message : 'Add context file error',
      });
    }
  };

  private readonly addTagToContext = async (chip: TagChip) => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        throw new Error('Context not found');
      }
      // TODO: server side docIds calculation
      const docIds = this.docDisplayConfig.getTagPageIds(chip.tagId);
      await AIProvider.context.addContextTag({
        contextId,
        tagId: chip.tagId,
        docIds,
      });
      this.updateChip(chip, {
        state: 'finished',
      });
    } catch (e) {
      this.updateChip(chip, {
        state: 'failed',
        tooltip: e instanceof Error ? e.message : 'Add context tag error',
      });
    }
  };

  private readonly addCollectionToContext = async (chip: CollectionChip) => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        throw new Error('Context not found');
      }
      // TODO: server side docIds calculation
      const docIds = this.docDisplayConfig.getCollectionPageIds(
        chip.collectionId
      );
      await AIProvider.context.addContextCollection({
        contextId,
        collectionId: chip.collectionId,
        docIds,
      });
      this.updateChip(chip, {
        state: 'finished',
      });
    } catch (e) {
      this.updateChip(chip, {
        state: 'failed',
        tooltip:
          e instanceof Error ? e.message : 'Add context collection error',
      });
    }
  };

  private readonly addAttachmentChipToContext = async (
    chip: AttachmentChip
  ) => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        throw new Error('Context not found');
      }
      await AIProvider.context.addContextBlob({
        blobId: chip.sourceId,
        contextId,
      });
      this.updateChip(chip, {
        state: 'finished',
        blobId: chip.sourceId,
      });
    } catch (e) {
      this.updateChip(chip, {
        state: 'failed',
        tooltip:
          e instanceof Error ? e.message : 'Add context attachment error',
      });
    }
  };

  private readonly removeFromContext = async (
    chip: ChatChip
  ): Promise<boolean> => {
    try {
      const contextId = await this.createContextId();
      if (!contextId || !AIProvider.context) {
        return true;
      }
      if (isDocChip(chip)) {
        return await AIProvider.context.removeContextDoc({
          contextId,
          docId: chip.docId,
        });
      }
      if (isFileChip(chip) && chip.fileId) {
        return await AIProvider.context.removeContextFile({
          contextId,
          fileId: chip.fileId,
        });
      }
      if (isTagChip(chip)) {
        return await AIProvider.context.removeContextTag({
          contextId,
          tagId: chip.tagId,
        });
      }
      if (isCollectionChip(chip)) {
        return await AIProvider.context.removeContextCollection({
          contextId,
          collectionId: chip.collectionId,
        });
      }
      if (isAttachmentChip(chip)) {
        return await AIProvider.context.removeContextBlob({
          contextId,
          blobId: chip.sourceId,
        });
      }
      if (isSelectedContextChip(chip)) {
        this.updateContext({
          ...this.chatContextValue,
          snapshot: null,
          combinedElementsMarkdown: null,
          canvasSelections: [],
        });
        const gfx = this.host?.std.getOptional(GfxControllerIdentifier);
        gfx?.selection.set({
          elements: [],
          editing: false,
        });
      }
      return true;
    } catch {
      return true;
    }
  };

  private readonly toggleChipsCollapse = () => {
    this.isChipsCollapsed = !this.isChipsCollapsed;
  };

  private readonly addImages = (images: File[]) => {
    const oldImages = this.chatContextValue.images;
    if (oldImages.length + images.length > MAX_IMAGE_COUNT) {
      this.notificationService.toast(
        `You can only upload up to ${MAX_IMAGE_COUNT} images`
      );
    }
    this.updateContext({
      images: [...oldImages, ...images].slice(0, MAX_IMAGE_COUNT),
    });
  };

  // sl-w252: PDF paste page-count thresholds
  private static readonly PDF_SOFT_WARNING_PAGES = 25;
  private static readonly PDF_HARD_BLOCK_PAGES = 51;

  /**
   * sl-w252: Add a pasted PDF as context. Checks page count and shows
   * appropriate dialog (soft-warning 25-50, hard-block >50).
   * Uses existing dontShowAgainKey infrastructure from NotificationService.
   * All dialog strings via i18n (com.streamlain.ai.pdf.*).
   */
  private readonly addPdf = async (file: File): Promise<void> => {
    const { pageCount, thumbnailDataUrl } = await extractPdfInfo(file);

    if (pageCount >= AIChatComposer.PDF_HARD_BLOCK_PAGES) {
      // >50 pages: Hard block — no cancel, no checkbox
      await this.notificationService.confirm({
        title: I18n.t('com.streamlain.ai.pdf.hard-block.title', {
          count: pageCount,
        }),
        message: I18n.t('com.streamlain.ai.pdf.hard-block.message'),
        confirmText: I18n.t('com.streamlain.ai.pdf.hard-block.confirm'),
      });
      return;
    }

    if (pageCount >= AIChatComposer.PDF_SOFT_WARNING_PAGES) {
      // 25-50 pages: Soft warning with "Don't show again" checkbox
      const confirmed = await this.notificationService.confirm({
        title: I18n.t('com.streamlain.ai.pdf.soft-warning.title', {
          count: pageCount,
        }),
        message: I18n.t('com.streamlain.ai.pdf.soft-warning.message'),
        confirmText: I18n.t('com.streamlain.ai.pdf.soft-warning.confirm'),
        cancelText: I18n.t('com.streamlain.ai.pdf.soft-warning.cancel'),
        dontShowAgainKey: 'pdf-paste-large-warning',
      });
      if (!confirmed) return;
    }

    // sl-w252: Store PDF file for sending, thumbnail renders in input area
    const pdf: PdfAttachment = {
      file,
      name: file.name || 'document.pdf',
      pageCount,
      thumbnailDataUrl,
      source: 'paste',
    };
    const currentPdfs = this.chatContextValue.pdfAttachments || [];
    this.updateContext({
      pdfAttachments: [...currentPdfs, pdf],
    });
  };

  private readonly removePdfAttachment = (pdf: PdfAttachment) => {
    const current = this.chatContextValue.pdfAttachments || [];
    this.updateContext({
      pdfAttachments: current.filter(p => p.name !== pdf.name),
    });
  };

  private readonly removePdfContextAttachment = (pdf: PdfContextAttachment) => {
    if (pdf.source === 'fullscreen') {
      this._dismissedFullscreenPdfBlockId = pdf.blockId;
    }
    const current = this.chatContextValue.pdfContextAttachments || [];
    this.updateContext({
      pdfContextAttachments: current.filter(p => p.blockId !== pdf.blockId),
    });
    this._deselectBlockContext(pdf.blockId);
  };

  private readonly removeDatabaseAttachment = (
    database: DatabaseAttachment
  ) => {
    const current = this.chatContextValue.databaseAttachments || [];
    this.updateContext({
      databaseAttachments: current.filter(
        db => db.blockId !== database.blockId
      ),
    });
    this._deselectBlockContext(database.blockId);
  };

  private readonly removeAutomationAttachment = (
    automation: AutomationAttachment
  ) => {
    const current = this.chatContextValue.automationAttachments || [];
    this.updateContext({
      automationAttachments: current.filter(
        a => a.blockId !== automation.blockId
      ),
    });
    this._deselectBlockContext(automation.blockId);
  };

  private readonly removeWorkflowAttachment = (
    workflow: WorkflowAttachment
  ) => {
    const current = this.chatContextValue.workflowAttachments || [];
    this.updateContext({
      workflowAttachments: current.filter(
        w => w.workflowId !== workflow.workflowId
      ),
    });
  };

  private readonly removeCodeSelection = (
    selection: CodeSelectionAttachment
  ) => {
    const current = this.chatContextValue.codeSelections || [];
    this.updateContext({
      codeSelections: current.filter(item => item.id !== selection.id),
    });
  };

  private readonly removeMailAttachment = (mail: MailMessageAttachment) => {
    const current = this.chatContextValue.mailAttachments || [];
    this.updateContext({
      mailAttachments: current.filter(item => item.id !== mail.id),
    });
    dispatchMailSelectionContextRemove({
      workspaceId: mail.workspaceId,
      messageId: mail.id,
    });
  };

  private readonly _deselectBlockContext = (blockId: string) => {
    const host = this.host;
    if (!host) return;

    const currentSelection = host.selection.value;
    const filteredSelection = currentSelection.filter(
      sel =>
        !('blockId' in sel && (sel as { blockId: string }).blockId === blockId)
    );
    if (filteredSelection.length !== currentSelection.length) {
      host.selection.set(filteredSelection);
    }

    const gfx = host.std.getOptional(GfxControllerIdentifier);
    if (!gfx) return;

    const selectedIds = (
      gfx.selection as {
        selectedIds?: string[];
        set: (selection: { elements: string[]; editing?: boolean }) => void;
      }
    ).selectedIds;
    if (!selectedIds?.includes(blockId)) return;

    gfx.selection.set({
      elements: selectedIds.filter(id => id !== blockId),
      editing: false,
    });
  };

  /**
   * sl-57s.1.3 + sl-57s.1.2: Get visible database attachments for display
   * Priority: paste > fullscreen > focus
   * - If paste exists: hide focus and fullscreen
   * - If fullscreen exists (no paste): hide focus
   * - Otherwise: show all
   */
  private _getVisibleDatabaseAttachments(): DatabaseAttachment[] {
    const attachments = normalizeDatabaseContextAttachments(
      this.chatContextValue.databaseAttachments || []
    );
    const hasManualAttachments = attachments.some(db => db.source === 'paste');
    const hasFullscreenAttachments = attachments.some(
      db => db.source === 'fullscreen'
    );

    if (hasManualAttachments) {
      // Paste has highest priority: hide all auto (focus + fullscreen)
      return attachments.filter(
        db => db.source !== 'focus' && db.source !== 'fullscreen'
      );
    }

    if (hasFullscreenAttachments) {
      // Fullscreen has higher priority than focus: hide focus
      return attachments.filter(db => db.source !== 'focus');
    }

    return attachments;
  }

  /**
   * Get visible automation attachments for display
   * Same priority logic as databases: paste > focus
   */
  private _getVisibleAutomationAttachments(): AutomationAttachment[] {
    const attachments = this.chatContextValue.automationAttachments || [];
    const hasManualAttachments = attachments.some(a => a.source === 'paste');

    if (hasManualAttachments) {
      return attachments.filter(a => a.source !== 'focus');
    }

    return attachments;
  }

  private readonly pollEmbeddingStatus = async () => {
    if (this._pollEmbeddingStatusAbortController) {
      this._pollEmbeddingStatusAbortController.abort();
    }
    this._pollEmbeddingStatusAbortController = new AbortController();
    const signal = this._pollEmbeddingStatusAbortController.signal;

    try {
      await AIProvider.context?.pollEmbeddingStatus(
        this.workspaceId,
        (status: ContextWorkspaceEmbeddingStatus) => {
          if (!status) {
            this.embeddingCompleted = false;
            return;
          }
          const prevCompleted = this.embeddingCompleted;
          const completed = status.embedded === status.total;
          this.embeddingCompleted = completed;
          if (prevCompleted !== completed) {
            this.requestUpdate();
          }
        },
        signal
      );
    } catch {
      this.embeddingCompleted = false;
    }
  };

  private readonly _abortPollEmbeddingStatus = () => {
    this._pollEmbeddingStatusAbortController?.abort();
    this._pollEmbeddingStatusAbortController = null;
  };

  private readonly initComposer = async () => {
    const userId = (await AIProvider.userInfo)?.id;
    if (!userId || !this.session) return;

    await this.initChips();
    await this.pollEmbeddingStatus();
  };
}
